import { useForm } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { DropdownMenuTrigger } from '@radix-ui/react-dropdown-menu'
import type { FilterStore } from 'components/Filters/FilterStore'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
} from 'components/UI/BaseDropdown/dropdown-base'
import { TextInput } from 'components/FormUtils/TextInput/text-input'
import FbButton from 'components/FbUI/FbButton'
import { FilterButton } from '../components/FilterButton'
import { FilterCloseButton } from '../components/FilterCloseButton'

const zodObject = z
  .object({
    min: z.string().optional().nullable(),
    max: z.string().optional().nullable(),
  })
  .superRefine((v, ctx) => {
    if (v.min !== null && v.max !== null && v.min !== '' && v.max !== '') {
      const min = new Date(v.min!)
      const max = new Date(v.max!)
      if (min > max) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Start date must be before end date',
          path: ['min'],
        })
      }
    }
  })

interface DatePreset {
  label: string
  value: string
  getDates: () => { min: string; max: string }
}

const getDatePresets = (): DatePreset[] => {
  const today = new Date()
  today.setHours(23, 59, 59, 999)

  const formatDate = (date: Date) => date.toISOString().split('T')[0]

  return [
    {
      label: 'Last Week',
      value: 'last_week',
      getDates: () => {
        const end = new Date(today)
        const start = new Date(today)
        start.setDate(start.getDate() - 7)
        start.setHours(0, 0, 0, 0)
        return { min: formatDate(start), max: formatDate(end) }
      },
    },
    {
      label: 'Last Month',
      value: 'last_month',
      getDates: () => {
        const end = new Date(today)
        const start = new Date(today)
        start.setMonth(start.getMonth() - 1)
        start.setHours(0, 0, 0, 0)
        return { min: formatDate(start), max: formatDate(end) }
      },
    },
    {
      label: 'Last 3 Months',
      value: 'last_3_months',
      getDates: () => {
        const end = new Date(today)
        const start = new Date(today)
        start.setMonth(start.getMonth() - 3)
        start.setHours(0, 0, 0, 0)
        return { min: formatDate(start), max: formatDate(end) }
      },
    },
    {
      label: 'Last 6 Months',
      value: 'last_6_months',
      getDates: () => {
        const end = new Date(today)
        const start = new Date(today)
        start.setMonth(start.getMonth() - 6)
        start.setHours(0, 0, 0, 0)
        return { min: formatDate(start), max: formatDate(end) }
      },
    },
    {
      label: 'Last Year',
      value: 'last_year',
      getDates: () => {
        const end = new Date(today)
        const start = new Date(today)
        start.setFullYear(start.getFullYear() - 1)
        start.setHours(0, 0, 0, 0)
        return { min: formatDate(start), max: formatDate(end) }
      },
    },
  ]
}

export function DateRangeFilterWithPresets({
  title,
  filterKey,
  filterStore,
  showPresets = true,
}: {
  title: string
  filterKey: string
  filterStore: FilterStore
  showPresets?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null)
  const {
    pendingRangeFilters,
    setPendingRangeFilters,
    clearPendingRangeFilter,
    registerFilterTitle,
  } = filterStore()
  const current = pendingRangeFilters?.[filterKey!]

  // Convert number to string for date fields
  const currentMin = current?.min != null ? String(current.min) : null
  const currentMax = current?.max != null ? String(current.max) : null

  const { control, watch, reset, setValue, handleSubmit } = useForm<{
    min: string | null
    max: string | null
  }>({
    defaultValues: {
      min: currentMin,
      max: currentMax,
    },
    resolver: zodResolver(zodObject),
  })

  function saveFilters() {
    const minValue = watch('min')
    const maxValue = watch('max')
    if (minValue !== null || maxValue !== null) {
      // Cast strings to any to bypass the type system since we're storing dates as strings
      setPendingRangeFilters(
        filterKey,
        (minValue as any) || undefined,
        (maxValue as any) || undefined
      )
    }

    setOpen(false)
  }

  const clearFilters = () => {
    reset({
      min: null,
      max: null,
    })
    setSelectedPreset(null)
    clearPendingRangeFilter(filterKey)
  }

  const handlePresetClick = (preset: DatePreset) => {
    const dates = preset.getDates()
    setValue('min', dates.min)
    setValue('max', dates.max)
    setSelectedPreset(preset.value)
  }

  const _handleSubmit = handleSubmit(() => {
    saveFilters()
    setOpen(false)
  })

  const getLabel = (): string => {
    if (!isRangeSet) {
      return 'All'
    }

    // Check if current values match any preset
    if (showPresets) {
      const matchingPreset = getDatePresets().find((preset) => {
        const dates = preset.getDates()
        return dates.min === currentMin && dates.max === currentMax
      })

      if (matchingPreset) {
        return matchingPreset.label
      }
    }

    // Check if a preset is selected and its dates match current values
    if (selectedPreset && showPresets) {
      const preset = getDatePresets().find((p) => p.value === selectedPreset)
      if (preset) {
        const dates = preset.getDates()
        if (dates.min === currentMin && dates.max === currentMax) {
          return preset.label
        }
      }
    }

    // Safely handle undefined values by falling back to `null`
    const min = currentMin
    const max = currentMax

    if (min === null && max !== null) {
      // Format date to shorter format
      const date = new Date(max)
      return `≤${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}`
    }

    if (max === null && min !== null) {
      // Format date to shorter format
      const date = new Date(min)
      return `≥${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}`
    }

    // Format both dates to shorter format to prevent overflow
    const startDate = new Date(min!)
    const endDate = new Date(max!)
    const formatOptions: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
    }

    // Only show year if dates are in different years
    const showYear =
      startDate.getFullYear() !== endDate.getFullYear() ||
      startDate.getFullYear() !== new Date().getFullYear()

    if (showYear) {
      formatOptions.year = '2-digit'
    }

    return `${startDate.toLocaleDateString('en-US', formatOptions)} - ${endDate.toLocaleDateString('en-US', formatOptions)}`
  }

  const isRangeSet = current?.min != null || current?.max != null

  useEffect(() => {
    registerFilterTitle(filterKey, title)
  }, [])

  // Check for matching preset when component mounts or filter values change
  useEffect(() => {
    if (!isRangeSet) {
      setSelectedPreset(null)
    } else if (showPresets && currentMin && currentMax) {
      // Check if current values match any preset
      const matchingPreset = getDatePresets().find((preset) => {
        const dates = preset.getDates()
        return dates.min === currentMin && dates.max === currentMax
      })

      if (matchingPreset) {
        setSelectedPreset(matchingPreset.value)
      }
    }
  }, [isRangeSet, currentMin, currentMax, showPresets])

  // Clear preset selection when dates are manually changed
  useEffect(() => {
    const subscription = watch((value) => {
      // Check if the current values match any preset
      const matchingPreset = getDatePresets().find((preset) => {
        const dates = preset.getDates()
        return dates.min === value.min && dates.max === value.max
      })

      if (matchingPreset) {
        // If values match a preset, select it
        setSelectedPreset(matchingPreset.value)
      } else if (!matchingPreset && selectedPreset) {
        // If values don't match any preset and one was selected, clear it
        setSelectedPreset(null)
      }
    })
    return () => subscription.unsubscribe()
  }, [watch])

  return (
    <DropdownMenu open={open} onOpenChange={(open) => setOpen(open)}>
      <div className={'relative'}>
        <DropdownMenuTrigger asChild>
          <FilterButton
            open={open}
            isActive={isRangeSet}
            title={title}
            activeValueLabel={getLabel()}
            onClick={() => setOpen(!open)}
          />
        </DropdownMenuTrigger>
        <FilterCloseButton
          isActive={isRangeSet}
          onClick={() => clearFilters()}
        />
      </div>

      <DropdownMenuContent className="w-64">
        {showPresets && (
          <>
            <div className="p-2">
              <div className="text-xs font-semibold text-gray-500 mb-2">
                Quick Select
              </div>
              {getDatePresets().map((preset) => (
                <button
                  key={preset.value}
                  onClick={() => handlePresetClick(preset)}
                  className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-gray-100 ${
                    selectedPreset === preset.value
                      ? 'bg-blue-50 text-blue-600'
                      : ''
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <DropdownMenuSeparator />
          </>
        )}

        <div className="p-2">
          <div className="text-xs font-semibold text-gray-500 mb-2">
            Custom Range
          </div>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
            className="mb-2"
          >
            <TextInput
              noMargin
              control={control}
              label="Start Date"
              name="min"
              type="date"
            />
            <TextInput
              noMargin
              control={control}
              label="End Date"
              name="max"
              type="date"
            />
          </div>
        </div>

        <div className="p-2 pt-0">
          <FbButton style={{ width: '100%' }} onClick={_handleSubmit}>
            Apply
          </FbButton>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
